/**
 * What to tell the user when a "send this to the client" action (invoice
 * receipt, statement) rejects.
 *
 * WHY this is not a hardcoded string. The app's only axios instance caps every
 * request at 30s (`utils/api.ts`). The invoice-email endpoint can exceed that
 * while the send is still IN FLIGHT — a cold `puppeteer.launch`, or a Postmark
 * call whose own ceiling is 60s. axios aborts the REQUEST; it does not abort
 * the EMAIL. Saying "Failed" there is a lie with a real cost: the user clicks
 * again and the client receives two receipts.
 *
 * So the branch is three-way, by what we actually know:
 *
 *   the server ANSWERED (any status)   -> show what it said; it knows the outcome
 *   no response + timeout evidence     -> outcome UNKNOWN. Never the word
 *                                         "Failed"; say to check before resending
 *   no response, no timeout evidence   -> offline / DNS / dead API: the caller's
 *                                         own fallback wording
 *
 * This deliberately does NOT guess a cause. It only stops the UI asserting one
 * it cannot know.
 */

export const SEND_PENDING_MESSAGE =
  'Still sending — this is taking longer than usual. The email may still be delivered; check with the client before sending again.';

export type SendErrorFeedback = {
  message: string;
  /** 'warning' for the unknown-outcome case — a red "error" toast reads as "it failed". */
  variant: 'error' | 'warning';
};

/**
 * Is this an axios error with EVIDENCE of a timeout? axios sets
 * `code: 'ECONNABORTED'` for a client-side abort and builds a
 * `timeout of {ms}ms exceeded` message for a real timeout; `ETIMEDOUT` is the
 * `clarifyTimeoutError` form. Either signal counts here — unlike
 * `offline/connectivity.ts`, which must exclude navigation aborts because it
 * flips APP-WIDE state. A user navigating away never sees this snackbar, so the
 * looser test costs nothing and catches every real timeout.
 */
export function looksLikeTimeout(error: any): boolean {
  const code = error?.code;
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true;
  return /timeout/i.test(String(error?.message ?? ''));
}

export function sendErrorMessage(error: any, fallback: string): SendErrorFeedback {
  const serverMessage = error?.response?.data?.message;
  if (error?.response) {
    // The server answered. Prefer its words; only fall back if the body carried
    // none (an empty 502 from a proxy, a non-JSON body).
    const message = Array.isArray(serverMessage) ? serverMessage.join(' ') : serverMessage;
    return { message: message || fallback, variant: 'error' };
  }
  if (looksLikeTimeout(error)) {
    return { message: SEND_PENDING_MESSAGE, variant: 'warning' };
  }
  return { message: fallback, variant: 'error' };
}
